import type { CurrentEmployerInput, PreviousEmployerInput, OtherSourceInput, IncomeEmploymentFormData, OtherSourceValidatedField, IncomeEmploymentValidationResult, PreviousJobGapRowInput, CurrentEmploymentApiPayload, PreviousEmploymentApiPayload, OtherSourceApiPayload } from '../types/utils'
import { recalculatePreviousEmployerJobGaps } from '@/features/applicant/utils/employmentJobGap'
import type { CurrentEmployer, PreviousEmployer } from '@/features/applicant/types/income'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const ZIP_DIGITS = /^\d{5,6}$/

const trim = (value: unknown): string => {
  if (value === null || value === undefined) return ""
  return String(value).trim()
}

/** Angular `companyEmailValidator` parity for HR/Manager Contact Email (optional field). */
export function hrManagerContactEmailError(value: unknown): string | null {
  const email = trim(value)
  if (!email) return null

  const emailParts = email.split("@")
  if (emailParts.length !== 2 || !EMAIL_PATTERN.test(email)) {
    return "Please enter a valid email address"
  }

  const domain = emailParts[1].toLowerCase()
  if (!/\.([a-z]{2,})$/i.test(domain)) {
    return "Email must end with a valid domain (.com, .in, etc.)"
  }

  return null
}

const numericOnly = (value: unknown): number => {
  const cleaned = trim(value).replace(/[^0-9.-]/g, "")
  if (!cleaned) return 0
  const num = Number(cleaned)
  return Number.isFinite(num) ? num : 0
}

const parseLocalDate = (value: string): Date | null => {
  const trimmed = trim(value)
  if (!trimmed) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split("-").map(Number)
    const date = new Date(y, m - 1, d)
    return Number.isNaN(date.getTime()) ? null : date
  }
  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const isFutureDate = (value: string): boolean => {
  const date = parseLocalDate(value)
  if (!date) return false
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return date.getTime() > today.getTime()
}

const isAfter = (start: string, end: string): boolean => {
  const a = parseLocalDate(start)
  const b = parseLocalDate(end)
  if (!a || !b) return false
  a.setHours(0, 0, 0, 0)
  b.setHours(0, 0, 0, 0)
  return a.getTime() > b.getTime()
}

const isPresentJob = (value: unknown): boolean =>
  value === true || value === "1" || value === 1

/** Moment-style whole month diff: `moment(later).diff(earlier, 'months')`. */
const monthDiffCalendar = (later: Date, earlier: Date): number => {
  const laterTime = later.getTime()
  const earlierTime = earlier.getTime()
  if (laterTime === earlierTime) return 0
  if (laterTime < earlierTime) return -monthDiffCalendar(earlier, later)

  let months =
    (later.getFullYear() - earlier.getFullYear()) * 12 +
    (later.getMonth() - earlier.getMonth())
  if (later.getDate() < earlier.getDate()) months -= 1
  return months
}


const parseOptionalJobGap = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined
  const num = Number(value)
  return Number.isFinite(num) ? num : undefined
}

const toApplicantPreviousEmployers = (
  rows: PreviousJobGapRowInput[],
): PreviousEmployer[] =>
  rows.map((row, index) => ({
    id: `application-prev-${index}`,
    employmentType: "full-time",
    companyName: "",
    address: "",
    otherOutsideUS: false,
    monthlyIncome: "",
    yearlyCalculatedIncome: "",
    jobStartDate: trim(row.start_date ?? ""),
    jobEndDate: trim(row.end_date ?? ""),
    monthsAtJob: "",
    jobGap: parseOptionalJobGap(row.job_gap),
    jobGapExplanation: row.job_gap_explanation ?? "",
  }))

const toApplicantCurrentEmployers = (
  rows: { start_date?: string }[],
): CurrentEmployer[] =>
  rows
    .map((row) => trim(row.start_date ?? ""))
    .filter(Boolean)
    .map((jobStartDate, index) => ({
      id: `application-current-${index}`,
      employmentType: "full-time",
      companyName: "",
      address: "",
      otherOutsideUS: false,
      monthlyIncome: "",
      yearlyCalculatedIncome: "",
      jobStartDate,
      presentJob: true,
      monthsAtJob: "",
      hrManagerEmail: "",
    }))

const formJobGapFromApplicant = (
  jobGap: number | undefined,
): string | number => {
  if (jobGap === undefined) return ""
  if (jobGap === 0) return "0"
  return jobGap
}

/** Job gap months — delegates to resident applicant `recalculatePreviousEmployerJobGaps`. */
export function computePreviousEmploymentJobGaps(
  currentRows: { start_date?: string }[],
  previousRows: PreviousJobGapRowInput[],
): Array<{ job_gap: string | number; job_gap_explanation: string }> {
  const recalculated = recalculatePreviousEmployerJobGaps(
    toApplicantPreviousEmployers(previousRows),
    toApplicantCurrentEmployers(currentRows),
  )

  return recalculated.map((row) => ({
    job_gap: formJobGapFromApplicant(row.jobGap),
    job_gap_explanation: row.jobGapExplanation ?? "",
  }))
}

export function mergePreviousJobGaps<T extends PreviousJobGapRowInput>(
  currentRows: { start_date?: string }[],
  previousRows: T[],
): T[] {
  const gaps = computePreviousEmploymentJobGaps(currentRows, previousRows)
  return previousRows.map((row, index) => ({
    ...row,
    job_gap: gaps[index]?.job_gap ?? "",
    job_gap_explanation: gaps[index]?.job_gap_explanation ?? "",
  }))
}

/** Show job gap block when `job_gap` truthy (Angular *ngIf; excludes 0). */
export function hasTruthyJobGapValue(jobGap: unknown): boolean {
  if (jobGap === null || jobGap === undefined || jobGap === "") return false
  if (jobGap === 0) return false
  return true
}

/** Angular `[ngClass]="{ 'd-none': job_gap.value == 0 }"` on explanation row. */
export function shouldShowJobGapExplanation(jobGap: unknown): boolean {
  if (!hasTruthyJobGapValue(jobGap)) return false
  return Number(jobGap) !== 0
}

/** Angular `[required]="job_gap.value >= 1"` on explanation textarea. */
export function isJobGapExplanationRequired(jobGap: unknown): boolean {
  const n = Number(jobGap)
  return Number.isFinite(n) && n >= 1
}

/** Angular jobPeriod — months between start/end (+1); current jobs use today when treatAsPresentJob. */
export function deriveJobDurationMonths(
  startDate: string,
  endDate?: string | null,
  options?: { treatAsPresentJob?: boolean },
): string {
  const startStr = trim(startDate)
  if (!startStr) return ""

  const start = parseLocalDate(startStr)
  if (!start) return ""

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  start.setHours(0, 0, 0, 0)

  if (start.getTime() >= today.getTime()) return "0"

  const treatAsPresent = options?.treatAsPresentJob ?? false
  const endStr = trim(endDate ?? "")
  if (!endStr && !treatAsPresent) return ""

  let end: Date
  if (treatAsPresent && !endStr) {
    end = new Date()
    end.setHours(0, 0, 0, 0)
  } else {
    const parsed = parseLocalDate(endStr)
    if (!parsed) return ""
    end = parsed
    end.setHours(0, 0, 0, 0)
  }

  const value = monthDiffCalendar(end, start) + 1
  return value <= 0 ? "0" : String(value)
}

const resolveJobDuration = (
  row: {
    job_duration?: string | number
    job_start_date?: string
    job_end_date?: string | null
    present_job?: string | boolean
  },
  treatAsPresentJob: boolean,
): string =>
  trim(row.job_duration) ||
  deriveJobDurationMonths(row.job_start_date ?? "", row.job_end_date, {
    treatAsPresentJob,
  })

const validateZipCode = (
  zip: unknown,
  errors: string[],
  prefix: string,
) => {
  const digits = trim(zip).replace(/\D/g, "")
  if (!digits) {
    errors.push(`${prefix}Employer Zipcode is required.`)
    return
  }
  if (!ZIP_DIGITS.test(digits)) {
    errors.push(`${prefix}Employer Zipcode must be 5 or 6 digits.`)
  }
}

const validateAddress = (
  row: CurrentEmployerInput,
  errors: string[],
  prefix: string,
) => {
  if (!trim(row.employer_street)) errors.push(`${prefix}Employer Street is required.`)
  if (!trim(row.employer_city)) errors.push(`${prefix}Employer City is required.`)
  if (!trim(row.employer_state)) errors.push(`${prefix}Employer State is required.`)
  validateZipCode(row.employer_zip_code, errors, prefix)
  if (!trim(row.employer_country)) errors.push(`${prefix}Country is required.`)
}

const validateCurrentEmployer = (
  row: CurrentEmployerInput,
  index: number,
  list: CurrentEmployerInput[],
  errors: string[],
) => {
  const label = `Current Employer ${index + 1}: `

  if (!trim(row.job_type)) errors.push(`${label}Type of Employment is required.`)

  const name = trim(row.employer_name)
  if (!name) {
    errors.push(`${label}Company Name is required.`)
  } else {
    const lower = name.toLowerCase()
    const dup = list.some(
      (other, otherIdx) =>
        otherIdx !== index &&
        trim(other.employer_name).toLowerCase() === lower,
    )
    if (dup) errors.push(`${label}This employer already exists.`)
  }

  const startDate = trim(row.job_start_date)
  if (!startDate) {
    errors.push(`${label}Job Start Date is required.`)
  }

  const incomeNumber = numericOnly(row.income)
  if (!incomeNumber || incomeNumber <= 0) {
    errors.push(`${label}Monthly Income is required.`)
  }

  const duration = resolveJobDuration(row, isPresentJob(row.present_job ?? true))
  if (!duration) {
    errors.push(`${label}Number of months at this job is required.`)
  }

  validateAddress(row, errors, label)

  const distance = Number(row.job_distance)
  if (
    Number.isFinite(distance) &&
    distance > 50 &&
    !trim(row.distance_explain)
  ) {
    errors.push(
      `${label}Situation/Explanation is required when property is more than 50 miles from employer.`,
    )
  }

  const hrEmailErr = hrManagerContactEmailError(row.hr_manager_contact_email)
  if (hrEmailErr) {
    errors.push(
      hrEmailErr === "Please enter a valid email address"
        ? `${label}HR/Manager Contact Email must be a valid email address.`
        : `${label}${hrEmailErr}`,
    )
  }

  if (!isPresentJob(row.present_job)) {
    const endDate = trim(row.job_end_date ?? "")
    if (endDate) {
      if (startDate && isAfter(startDate, endDate)) {
        errors.push(`${label}Job End Date should be after Job Start Date.`)
      }
      if (isFutureDate(endDate)) {
        errors.push(`${label}Job End Date should not be in the Future.`)
      }
    }
  }
}

const validatePreviousEmployer = (
  row: PreviousEmployerInput,
  index: number,
  errors: string[],
) => {
  const label = `Previous Employer ${index + 1}: `

  if (!trim(row.job_type)) errors.push(`${label}Type of Employment is required.`)

  const name = trim(row.employer_name)
  if (!name) errors.push(`${label}Company Name is required.`)

  const startDate = trim(row.job_start_date)
  if (!startDate) {
    errors.push(`${label}Job Start Date is required.`)
  } else if (isFutureDate(startDate)) {
    errors.push(`${label}Job Start Date should not be in the Future.`)
  }

  const endDate = trim(row.job_end_date ?? "")
  if (!endDate) {
    errors.push(`${label}Job End Date is required.`)
  } else {
    if (startDate && isAfter(startDate, endDate)) {
      errors.push(`${label}Job End Date should be after Job Start Date.`)
    }
    if (isFutureDate(endDate)) {
      errors.push(`${label}Job End Date should not be in the Future.`)
    }
  }

  const incomeNumber = numericOnly(row.income)
  if (!incomeNumber || incomeNumber <= 0) {
    errors.push(`${label}Monthly Income is required.`)
  }

  const duration = resolveJobDuration(row, false)
  if (!duration) {
    errors.push(`${label}Number of months at this job is required.`)
  }

  validateAddress(row, errors, label)

  const gap = Number(row.job_gap)
  if (Number.isFinite(gap) && gap >= 1) {
    const explanation = trim(row.job_gap_explanation)
    if (!explanation) {
      errors.push(
        `${label}Job Gap Explanation is required when gap is 1 month or more.`,
      )
    } else if (explanation.length > 200) {
      errors.push(
        `${label}Job Gap Explanation must be 200 characters or less.`,
      )
    }
  }
}

const validateOtherSource = (
  row: OtherSourceInput,
  index: number,
  errors: string[],
  rowErrors: Partial<Record<OtherSourceValidatedField, string>>,
) => {
  const label = `Other Income ${index + 1}: `
  const setField = (field: OtherSourceValidatedField, message: string) => {
    errors.push(`${label}${message}`)
    if (!rowErrors[field]) rowErrors[field] = message
  }

  const period = trim(row.income_period)
  if (!period) {
    setField("income_period", "Income Frequency is required.")
    return
  }

  if (!trim(row.source_name)) {
    setField("source_name", "Source Name is required.")
  }

  const incomeNumber = numericOnly(row.income)
  if (!incomeNumber || incomeNumber <= 0) {
    setField("income", "Income/Amount is required.")
  }

  const startDate = trim(row.job_start_date)
  if (!startDate) {
    setField(
      "job_start_date",
      period === "One_Time"
        ? "Transaction Date is required."
        : "Start Date is required.",
    )
  } else if (isFutureDate(startDate)) {
    setField("job_start_date", "Date should not be in the Future.")
  }

  if (period !== "One_Time") {
    if (!trim(row.job_duration)) {
      setField("job_duration", "Months of this Income is required.")
    }
    if (!isPresentJob(row.present_job)) {
      const endDate = trim(row.job_end_date ?? "")
      if (!endDate) {
        setField("job_end_date", "End Date is required.")
      } else {
        if (startDate && isAfter(startDate, endDate)) {
          setField("job_end_date", "End Date cannot be before Start Date.")
        }
        if (isFutureDate(endDate)) {
          setField("job_end_date", "Date should not be in the Future.")
        }
      }
    }
  }
}

const isRowMeaningful = (row: CurrentEmployerInput): boolean => {
  return Boolean(
    trim(row.employer_name) ||
      trim(row.employer_street) ||
      trim(row.employer_city) ||
      trim(row.income) ||
      trim(row.job_start_date),
  )
}

const isOtherSourceMeaningful = (row: OtherSourceInput): boolean => {
  return Boolean(
    trim(row.source_name) || trim(row.income) || trim(row.job_start_date),
  )
}

/** Matches Angular resident-application-add: warn when Not Employed with no previous/other income. */
export function requiresNoEmploymentWarning(
  data: IncomeEmploymentFormData,
): boolean {
  if (data.employment_status !== "0") return false

  const hasPrevious = (data.previous_employment ?? []).some(isRowMeaningful)
  if (hasPrevious) return false

  const otherRows = data.other_source ?? []
  if (otherRows.length > 0) return false

  return true
}

export function validateIncomeEmploymentTab(
  data: IncomeEmploymentFormData,
): IncomeEmploymentValidationResult {
  const errors: string[] = []
  const status = data.employment_status

  if (status !== "0" && status !== "1") {
    errors.push("Employment Status is required.")
    return { isValid: false, errors }
  }

  const currentList =
    status === "1" ? (data.current_employment ?? []).filter(isRowMeaningful) : []
  const previousList = (data.previous_employment ?? []).filter(isRowMeaningful)
  const otherListAll = data.other_source ?? []
  const otherListMeaningful = otherListAll.filter(isOtherSourceMeaningful)

  if (
    status === "1" &&
    currentList.length === 0 &&
    otherListMeaningful.length === 0 &&
    otherListAll.length === 0
  ) {
    errors.push(
      "Please add at least one current employer or other income source.",
    )
  }

  const otherSourceFieldErrors: Partial<
    Record<OtherSourceValidatedField, string>
  >[] = otherListAll.map(() => ({}))

  currentList.forEach((row, idx) =>
    validateCurrentEmployer(row, idx, currentList, errors),
  )
  previousList.forEach((row, idx) => validatePreviousEmployer(row, idx, errors))
  // Angular validates every row in the other_source FormArray, including newly added empty rows.
  otherListAll.forEach((row, idx) =>
    validateOtherSource(row, idx, errors, otherSourceFieldErrors[idx]!),
  )

  const hasOtherRowErrors = otherSourceFieldErrors.some(
    (row) => Object.keys(row).length > 0,
  )

  return {
    isValid: errors.length === 0,
    errors,
    ...(hasOtherRowErrors ? { otherSourceFieldErrors } : {}),
  }
}


const formatYmd = (value: string | undefined): string => {
  const trimmed = trim(value)
  if (!trimmed) return ""
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [m, d, y] = trimmed.split("/")
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
  }
  const date = parseLocalDate(trimmed)
  if (!date) return trimmed
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

const baseEmployerPayload = (
  row: CurrentEmployerInput,
  options?: { treatAsPresentJob?: boolean },
): Omit<CurrentEmploymentApiPayload, "present_job" | "job_end_date"> => {
  const incomeValue = numericOnly(row.income)
  const treatAsPresent =
    options?.treatAsPresentJob ?? isPresentJob(row.present_job ?? true)
  return {
    job_id: row.job_id ?? "",
    job_type: trim(row.job_type) || "FULL_TIME",
    employer_name: trim(row.employer_name),
    employer_street: trim(row.employer_street) || undefined,
    employer_city: trim(row.employer_city) || undefined,
    employer_state: trim(row.employer_state) || undefined,
    employer_zip_code: trim(row.employer_zip_code) || undefined,
    employer_country: trim(row.employer_country) || undefined,
    income: incomeValue > 0 ? String(incomeValue) : "",
    income_period: trim(row.income_period) || "Per_Month",
    job_duration: resolveJobDuration(row, treatAsPresent) || 0,
    job_start_date: formatYmd(row.job_start_date) || undefined,
    check_types: trim(row.check_types) || undefined,
    hr_manager_contact_email: trim(row.hr_manager_contact_email) || undefined,
    employer_website: trim(row.employer_website) || undefined,
    outside_us: row.outside_us === true || row.outside_us === "1",
    job_distance:
      row.job_distance !== undefined && row.job_distance !== ""
        ? Number(row.job_distance)
        : undefined,
    distance_explain: trim(row.distance_explain) || undefined,
  }
}

export function mapCurrentEmployerToApi(
  row: CurrentEmployerInput,
): CurrentEmploymentApiPayload {
  const present = isPresentJob(row.present_job ?? true)
  const base = baseEmployerPayload(row, { treatAsPresentJob: present })
  return {
    ...base,
    present_job: present ? "1" : "0",
    job_end_date: present ? null : formatYmd(row.job_end_date ?? "") || undefined,
  }
}

export function mapPreviousEmployerToApi(
  row: PreviousEmployerInput,
): PreviousEmploymentApiPayload {
  const base = baseEmployerPayload(row, { treatAsPresentJob: false })
  return {
    ...base,
    job_end_date: formatYmd(row.job_end_date ?? "") || undefined,
    job_gap: row.job_gap !== undefined && row.job_gap !== "" ? row.job_gap : undefined,
    job_gap_explanation: trim(row.job_gap_explanation) || undefined,
  }
}


export function mapOtherSourceToApi(row: OtherSourceInput): OtherSourceApiPayload {
  const present = isPresentJob(row.present_job)
  const incomeValue = numericOnly(row.income)
  const period = trim(row.income_period) || "Per_Month"
  return {
    job_id: row.job_id ?? "",
    source_name: trim(row.source_name),
    income: incomeValue > 0 ? String(incomeValue) : "",
    income_period: period,
    job_start_date: formatYmd(row.job_start_date) || undefined,
    job_end_date:
      period === "One_Time"
        ? formatYmd(row.job_start_date) || null
        : present
          ? null
          : formatYmd(row.job_end_date ?? "") || null,
    job_duration: period === "One_Time" ? "0" : trim(row.job_duration) || "0",
    present_job: present ? "1" : "0",
    check_types: trim(row.check_types) || undefined,
  }
}

export type {
  CurrentEmployerInput,
  PreviousEmployerInput,
  OtherSourceInput,
  OtherSourceValidatedField,
  IncomeEmploymentFormData,
} from '../types/utils'

